type Envelope = {
  id: string;
  userId: string;
  at: number;
  expires: number;
  iv: Uint8Array;
  data: ArrayBuffer;
};
function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open("gabinete-offline-v1", 1);
    r.onupgradeneeded = () =>
      r.result.createObjectStore("items", { keyPath: "id" });
    r.onsuccess = () => resolve(r.result);
    r.onerror = () =>
      reject(Error("Não foi possível abrir o armazenamento deste aparelho."));
  });
}
async function operation<T>(
  mode: IDBTransactionMode,
  action: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await database();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("items", mode),
      req = action(tx.objectStore("items"));
    let result: T;
    req.onsuccess = () => {
      result = req.result;
    };
    tx.oncomplete = () => {
      db.close();
      resolve(result);
    };
    tx.onerror = () => {
      db.close();
      reject(
        Error(
          "Não foi possível salvar neste aparelho. Seu formulário foi preservado.",
        ),
      );
    };
    tx.onabort = tx.onerror;
  });
}
const read = (id: string) => operation<any>("readonly", (s) => s.get(id));
const write = (value: any) => operation("readwrite", (s) => s.put(value));
const remove = (id: string) => operation("readwrite", (s) => s.delete(id));
export async function localOfflineEnabled(userId: string) {
  return (await read("opt:" + userId))?.enabled === true;
}
export async function setLocalOffline(userId: string, enabled: boolean) {
  if (!enabled) await clearOffline(userId);
  else await write({ id: "opt:" + userId, userId, enabled: true });
}
export async function offlinePending(userId: string): Promise<Envelope[]> {
  return (await operation<any[]>("readonly", (s) => s.getAll())).filter(
    (v) => v.userId === userId && v.id.startsWith("demand:"),
  );
}
export async function clearOffline(userId: string) {
  for (const v of await operation<any[]>("readonly", (s) => s.getAll()))
    if (v.userId === userId) await remove(v.id);
}
async function encryptionKey(userId: string) {
  let saved = await read("key:" + userId);
  if (!saved) {
    saved = {
      id: "key:" + userId,
      userId,
      key: await crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"],
      ),
    };
    await write(saved);
  }
  return saved.key as CryptoKey;
}
export async function queueDemand(
  userId: string,
  body: unknown,
  retentionHours: number,
) {
  if (!(await localOfflineEnabled(userId)))
    throw Error(
      "Ative o armazenamento neste aparelho em Meu perfil antes de salvar pendências offline.",
    );
  const payload = JSON.stringify(body);
  if (payload.length > 200000)
    throw Error("O conteúdo excede o limite de armazenamento offline.");
  const existing = await offlinePending(userId);
  if (existing.length >= 20)
    throw Error(
      "Há 20 pendências neste aparelho. Envie ou remova uma antes de continuar.",
    );
  const iv = crypto.getRandomValues(new Uint8Array(12)),
    key = await encryptionKey(userId),
    id = "demand:" + userId + ":" + (body as any).operationId;
  const data = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: new TextEncoder().encode(id) },
    key,
    new TextEncoder().encode(payload),
  );
  await write({
    id,
    userId,
    iv,
    data,
    at: Date.now(),
    expires: Date.now() + Math.min(retentionHours, 72) * 3600000,
  });
  window.dispatchEvent(new Event("offline-pending-changed"));
}
export async function openPending(row: Envelope) {
  if (row.expires < Date.now())
    throw Error(
      "A validade desta pendência expirou. Remova-a e confira os dados antes de registrar novamente.",
    );
  const plain = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: new Uint8Array(row.iv),
      additionalData: new TextEncoder().encode(row.id),
    },
    await encryptionKey(row.userId),
    row.data,
  );
  return JSON.parse(new TextDecoder().decode(plain));
}
export async function removePending(id: string) {
  await remove(id);
  window.dispatchEvent(new Event("offline-pending-changed"));
}
